import { useState } from "react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { COACH } from "@/constants/testIds";
import { toast } from "sonner";
import { Briefcase, FileText, Mail, Compass, Sparkles } from "lucide-react";

const KINDS = [
  { key: "cv", label: "CV", icon: FileText },
  { key: "cover_letter", label: "Cover Letter", icon: Mail },
  { key: "interview_prep", label: "Interview Prep", icon: Briefcase },
  { key: "roadmap", label: "Career Roadmap", icon: Compass },
];

export default function CoachPage() {
  const [kind, setKind] = useState("cv");
  const [context, setContext] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!context.trim()) { toast.error("Add a bit of context first."); return; }
    setLoading(true);
    setOutput("");
    try {
      const { data } = await api.post("/coach", { kind, context });
      setOutput(data.output);
    } catch { toast.error("Coach unavailable"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid={COACH.root} className="space-y-8 animate-fade-up">
      <div>
        <div className="label-mono text-primary">// career coach</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">AI Career Coach.</h1>
        <p className="mt-1 text-sm text-muted-foreground">Generate a CV, cover letter, mock interview prep, and a 12-month roadmap.</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="label-mono mb-3">Choose a task</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {KINDS.map((k) => (
            <button
              key={k.key}
              data-testid={COACH.kindSelect(k.key)}
              onClick={() => setKind(k.key)}
              className={`flex items-center gap-3 rounded-xl border p-4 text-left transition ${kind === k.key ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
            >
              <k.icon className={`h-5 w-5 ${kind === k.key ? "text-primary" : "text-muted-foreground"}`} />
              <div className="font-display font-bold">{k.label}</div>
            </button>
          ))}
        </div>

        <div className="mt-6">
          <div className="label-mono">Your context</div>
          <Textarea
            data-testid={COACH.contextInput}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Tell your coach about your experience, target role, target company, and key skills…"
            className="mt-2 min-h-[140px] rounded-lg"
          />
          <Button
            data-testid={COACH.generateBtn}
            onClick={generate}
            disabled={loading}
            className="mt-4 rounded-full btn-neon font-mono text-xs uppercase tracking-[0.2em]"
          >
            <Sparkles className="mr-2 h-4 w-4" /> {loading ? "Generating…" : "Generate"}
          </Button>
        </div>
      </div>

      {output && (
        <div data-testid={COACH.output} className="rounded-2xl border border-primary/30 bg-primary/5 p-6">
          <div className="label-mono mb-3 text-primary">// generated output</div>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{output}</pre>
        </div>
      )}
    </div>
  );
}
